"use client"

import { ChevronRight, Home } from "lucide-react"
import { Fragment } from "react"
import { BreadcrumbList, WithContext } from "schema-dts"

import { TrackedLink } from "@/components/analytics"
import { BASE_URL } from "@/constants/base-url"

import Container from "./Container"
import StructuredData from "./StructuredData"
interface BreadcrumbItem {
  href?: string
  label?: string
  placeholder?: boolean
}

interface BreadcrumbProps {
  className?: string
  items: BreadcrumbItem[]
}

export default function Breadcrumb({ className = "", items }: BreadcrumbProps) {
  const breadcrumbStructuredData: WithContext<BreadcrumbList> = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      item: item.href
        ? { "@id": `${BASE_URL}${item.href}`, name: item.label }
        : undefined,
      name: item.label,
      position: index + 1
    }))
  }

  return (
    <>
      <StructuredData structuredData={breadcrumbStructuredData} />
      <Container
        aria-label="Breadcrumb"
        className={`flex items-center gap-2 text-sm embed:hidden print:hidden my-4 ${className}`}
        tag="nav"
      >
        <TrackedLink
          action="Breadcrumb Clicked"
          category="navigation"
          className="flex gap-2 items-center text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
          href="/"
          label="Home"
        >
          <Home className="w-4 h-4" />
          <span className="hidden md:block">Home</span>
        </TrackedLink>

        {items.map((item, index) => (
          <Fragment key={index}>
            <ChevronRight
              className={`w-4 h-4 text-slate-400 dark:text-slate-500 ${
                index === 0 ? "hidden md:block" : ""
              }`}
            />
            {item.placeholder ? (
              <div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
            ) : item.href ? (
              <TrackedLink
                action="Breadcrumb Clicked"
                category="navigation"
                className={`text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors ${
                  index === 0
                    ? "hidden md:block"
                    : "md:max-w-none max-w-[100px] overflow-hidden whitespace-nowrap text-ellipsis"
                }`}
                href={item.href}
                label={item.label || ""}
              >
                {item.label}
              </TrackedLink>
            ) : (
              <span className="text-slate-500 dark:text-slate-400">
                {item.label}
              </span>
            )}
          </Fragment>
        ))}
      </Container>
    </>
  )
}
